import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Product, WeightUnit } from '@/lib/types'
import { formatQuantity, formatUsd } from '@/lib/money'
import { abbreviate } from '@/pages/checkout/WeighDialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal, Select } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { t } from '@/i18n'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'

/**
 * Why stock left without being sold.
 *
 * Kept to the shop's own words. "Damaged" covers the broken sack; "Count shortage" is the honest
 * label for stock that is simply not there, which is different from knowing it was stolen.
 */
const REASONS: { value: string; label: string; hint: string }[] = [
  { value: 'Expired', label: t('Past its date'), hint: 'Perishable stock that can no longer be sold' },
  { value: 'Damaged', label: t('Damaged or broken'), hint: 'Spoiled, crushed, spilled or broken open' },
  { value: 'Theft', label: t('Stolen'), hint: 'Known or strongly suspected — reported separately' },
  { value: 'Sample', label: t('Given away'), hint: 'A tasting, a sample or a goodwill giveaway' },
  { value: 'InternalUse', label: t('Used by the shop'), hint: 'Consumed by the business itself' },
  { value: 'CountShortage', label: t('Missing at a count'), hint: 'Not on the shelf, with no known cause' },
]

const WEIGHT_UNITS: { value: WeightUnit; label: string }[] = [
  { value: 'Tonne', label: t('Tonnes') },
  { value: 'Kilogram', label: t('Kilograms') },
  { value: 'Gram', label: t('Grams') },
]

function isWeightUnit(unit: string): unit is WeightUnit {
  return unit === 'Tonne' || unit === 'Kilogram' || unit === 'Gram'
}

/**
 * Takes stock off the shelf without selling it.
 *
 * Written off at what the goods cost, never at what they would have sold for — waste is a loss of
 * money already spent, not of profit that was never earned. The name of whoever recorded it is
 * kept with it, because declaring stock spoiled is the easiest way to walk it out of a shop.
 */
export function WasteDialog({
  product,
  onClose,
}: {
  product: Product | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const ownBranchId = useSession((state) => state.session)?.branchId ?? null

  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState<WeightUnit | null>(null)
  const [reason, setReason] = useState('Damaged')
  const [note, setNote] = useState('')
  const [branchId, setBranchId] = useState<number | ''>(ownBranchId ?? '')
  const [error, setError] = useState<string | null>(null)

  // Stock leaves one branch's shelf, so an admin — who is tied to none — has to say which.
  // A branch user never sees this; the server pins them to their own regardless.
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiClient.get<{ id: number; displayName: string }[]>('/api/branches'),
    enabled: ownBranchId === null && product !== null,
  })

  const scoped = ownBranchId ?? (branchId === '' ? null : Number(branchId))

  useEffect(() => {
    if (product) {
      setQuantity('')
      setReason(product.trackExpiry ? 'Expired' : 'Damaged')
      setNote('')
      setError(null)
      // Starts in the unit the item is held in, which is always recordable; a finer one may not be.
      setUnit(product.sellsByWeight && isWeightUnit(product.unit) ? product.unit : null)
    }
  }, [product])

  const record = useMutation({
    mutationFn: () =>
      apiClient.post<{ entries: number; totalCostUsd: number; quantity: number }>('/api/waste', {
        productId: product!.id,
        branchId: scoped,
        stockBatchId: null,
        quantity: Number(quantity || 0),
        quantityUnit: unit,
        reason,
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      await queryClient.invalidateQueries({ queryKey: ['waste'] })
      await queryClient.invalidateQueries({ queryKey: ['low-stock'] })
      onClose()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not write that off.',
      ),
  })

  const chosen = REASONS.find((entry) => entry.value === reason)
  const stockUnit = product ? abbreviate(product.unit) : ''
  const entered = Number(quantity || 0)

  return (
    <Modal
      open={product !== null}
      onClose={onClose}
      title={product ? `Write off ${product.name}` : ''}
      description={
        product
          ? `${formatQuantity(product.quantityOnHand)}${stockUnit ? ` ${stockUnit}` : ''} on hand · costs ${formatUsd(product.costUsd)}`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => record.mutate()}
            disabled={record.isPending || entered <= 0 || scoped === null}
          >
            {record.isPending ? 'Writing off…' : 'Write it off'}
          </Button>
        </>
      }
    >
      {ownBranchId === null && (
        <Select
          label={t('Which branch')}
          value={branchId}
          wrapperClassName="mb-4"
          onChange={(event) =>
            setBranchId(event.target.value === '' ? '' : Number(event.target.value))
          }
        >
          <option value="">{t('Choose a branch…')}</option>
          {branches.data?.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.displayName}
            </option>
          ))}
        </Select>
      )}

      {/* A scale item is written off by weight, in whichever unit it was measured in. */}
      {product?.sellsByWeight && (
        <div className="mb-3 flex flex-wrap gap-2">
          {WEIGHT_UNITS.map((option) => (
            <Button
              key={option.value}
              variant={unit === option.value ? 'primary' : 'secondary'}
              size="sm"
              className="touch-target flex-1"
              onClick={() => setUnit(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )}

      <Input
        label={
          product?.sellsByWeight && unit
            ? `How much, in ${abbreviate(unit)}`
            : `How many${stockUnit ? ` (${stockUnit})` : ''}`
        }
        type="number"
        inputMode="decimal"
        step="any"
        min="0"
        value={quantity}
        onChange={(event) => setQuantity(event.target.value)}
        className="tabular text-lg"
        placeholder="0"
        autoFocus
      />

      <Select
        label={t('Why')}
        value={reason}
        wrapperClassName="mt-4"
        onChange={(event) => setReason(event.target.value)}
      >
        {REASONS.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {entry.label}
          </option>
        ))}
      </Select>
      {chosen && (
        <p className="mt-1 text-xs text-[var(--text-secondary)]">{chosen.hint}</p>
      )}

      <Input
        label={t('Note')}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        wrapperClassName="mt-4"
        placeholder={t('Optional — what happened')}
      />

      <p className="surface-sunken mt-4 rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
        Taken from the soonest-expiring stock first, and valued at what that stock cost — not at
        its selling price. Your name is kept with it.
      </p>

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          <TriangleAlert className="mr-1 inline h-4 w-4" />
          {error}
        </p>
      )}
    </Modal>
  )
}

interface WasteRow {
  id: number
  productName: string | null
  quantity: number
  unit: string
  totalCostUsd: number
  reason: string
  note: string | null
  occurredAtUtc: string
  approvedBy: string | null
  isReversed: boolean
  reversalReason: string | null
  reversedBy: string | null
}

/** Recent write-offs, so the shop can see what it is losing and who recorded it. */
export function WasteHistory() {
  /** The write-off being put back. */
  const [reversing, setReversing] = useState<WasteRow | null>(null)

  const [filters, setFilters] = useState<ListFilterState>(NO_FILTERS)

  const waste = usePagedList<WasteRow>(['waste'], '/api/waste', {
    from: filters.from,
    to: filters.to,
  })

  if (waste.page.length === 0 && !waste.isLoading && filters.from === '' && filters.to === '') {
    return (
      <p className="surface-sunken rounded-lg px-3 py-6 text-center text-sm text-[var(--text-secondary)]">
        {t('Nothing has been written off.')}
      </p>
    )
  }

  // A reversed write-off is no longer a loss, so it drops out of the total while staying on
  // screen — the pattern of what gets written off and then put back is worth seeing.
  const live = waste.page.filter((entry) => !entry.isReversed)
  const total = live.reduce((sum, entry) => sum + entry.totalCostUsd, 0)

  return (
    <>
      {/* No search box: a write-off has no number to quote, so the useful question is "when". */}
      <ListFilters value={filters} onChange={setFilters} searchHint="" showSearch={false} />

      <p className="tabular mb-3 text-sm text-[var(--text-secondary)]">
        {waste.hasNextPage
          ? t('{amount} across the {shown} shown', {
              amount: formatUsd(total),
              shown: live.length,
            })
          : t('{count} write-offs · {amount} at cost', {
              count: live.length,
              amount: formatUsd(total),
            })}
      </p>

      <ul className="flex flex-col gap-2">
        {waste.page.map((entry) => (
          <li
            key={entry.id}
            className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
          >
            <Trash2 className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p
                  className={`truncate text-sm font-medium ${
                    entry.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                  }`}
                >
                  {entry.productName ?? '—'}
                </p>
                {entry.isReversed && (
                  <ReversedBadge reason={entry.reversalReason} by={entry.reversedBy} />
                )}
              </div>
              <p className="truncate text-xs text-[var(--text-secondary)]">
                {REASONS.find((r) => r.value === entry.reason)?.label ?? entry.reason}
                {entry.note ? ` · ${entry.note}` : ''}
                {entry.approvedBy ? ` · ${entry.approvedBy}` : ''}
                {entry.isReversed && entry.reversalReason ? ` · put back: ${entry.reversalReason}` : ''}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p
                className={`tabular text-sm font-medium ${
                  entry.isReversed ? 'text-[var(--text-secondary)] line-through' : ''
                }`}
              >
                {formatUsd(entry.totalCostUsd)}
              </p>
              <p className="tabular text-xs text-[var(--text-secondary)]">
                {formatQuantity(entry.quantity)} {abbreviate(entry.unit)}
              </p>
            </div>

            <ReverseButton
              isReversed={entry.isReversed}
              label={`Put back ${entry.productName ?? 'this write-off'}`}
              onClick={() => setReversing(entry)}
            />
          </li>
        ))}
      </ul>

      <Pager list={waste} />

      <ReverseDialog
        kind="waste"
        id={reversing?.id ?? null}
        what={
          reversing
            ? `${reversing.productName ?? 'Item'} — ${formatQuantity(reversing.quantity)} ${abbreviate(reversing.unit)} at ${formatUsd(reversing.totalCostUsd)}`
            : ''
        }
        onClose={() => setReversing(null)}
        invalidate={['waste', 'products', 'stock', 'low-stock', 'reports']}
      />
    </>
  )
}
